// kvList.ts — thin helper for "load array / save array" IDB patterns.
//
// Before this helper (M4 audit item 2026-05-13) every store defined its
// own identical pair of loadAll() / saveAll() functions. The only difference
// between stores was the IDB key and the TypeScript generic.
//
// Usage:
//   import { createKvList } from '../lib/kvList';
//   const { loadAll, saveAll } = createKvList<MyType>('fyrefly:things:v1');

import { kvGet, kvSet } from './idb';

export interface KvList<T> {
  /** Load the persisted list, returning [] if absent or malformed. */
  loadAll(): Promise<T[]>;
  /** Overwrite the persisted list with `items`. */
  saveAll(items: T[]): Promise<void>;
}

/**
 * Create a typed load/save pair backed by IDB for a single list key.
 *
 * @param key  IDB key, e.g. 'fyrefly:incidents:v1'.
 */
export function createKvList<T>(key: string): KvList<T> {
  return {
    async loadAll(): Promise<T[]> {
      const raw = await kvGet<T[]>(key);
      return Array.isArray(raw) ? raw : [];
    },
    async saveAll(items: T[]): Promise<void> {
      await kvSet(key, items);
    },
  };
}
